import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, BookingService, ToastService, UtilityService } from '../../../../../services';
import { TutorCard, TutorSearch, UserObj } from '../../../../../microservice-clients/user';
import { AvailabilityByDayModel } from '../../../../../models';
import { MessageType } from '../../../../../constants';
import { TranslateModule } from '@ngx-translate/core';
import { MatTabsModule } from '@angular/material/tabs';
import { SlideToggleComponent } from '../../../../../standalone-components/slide-toggle/slide-toggle.component';
import { UserStateEnum } from '../../../../../microservice-clients/user';

@Component({
    selector: 'app-general-availability-calendar',
    imports: [CommonModule, MatTabsModule, TranslateModule, SlideToggleComponent],
    templateUrl: './general-availability-calendar.component.html',
    styleUrls: ['./general-availability-calendar.component.scss']
})
export class GeneralAvailabilityCalendarComponent implements OnInit {

  @Input() tutorCard: TutorSearch | TutorCard;
  @Input() isTogglesHidden: boolean = false;
  @Input() acceptsNewStudents: string;
  @Input() applyCustomStyling: boolean = false;
  @Input() hideTitleAndDescription: boolean = false;

  tutorId: string;
  tutorName: string;
  isStudent: boolean;
  tutorAvailability: AvailabilityByDayModel[] = [];
  dayShortLabels: string[] = [];
  isCalendarEmpty: boolean = true;
  firstAvailableDayIndex: number;
  popularSlots: Set<string> = new Set();


  tooltipTextAvailable: string;
  tooltipTextNotAvailable: string;

  private isUpdatingAvailability = false;

  get UserStateEnum(): typeof UserStateEnum {
    return UserStateEnum;
  }

  constructor(
    public authService: AuthService,
    private bookingService: BookingService,
    private toastService: ToastService,
    private utilityService: UtilityService
  ) { }

  ngOnInit(): void {
    this.isStudent = !!this.tutorCard;
    if (this.isStudent) {
      this.tutorId = this.tutorCard.tutor.id.toString();
      this.tutorName = this.tutorCard.tutor.personalInfo.forename;
    } else {
      this.tutorId = this.authService.user.id.toString();
      this.authService.userDetails.tutorDetails._canTeachOnline = true;
    }
    this.getAvailability();
  }

  private getAvailability(onComplete?: () => void): void {
    this.bookingService.getTutorWeeklyAvailability(this.tutorId).subscribe({
      next: (response) => {
        let parsedAvailability = [];
        this.isCalendarEmpty = true;

        for (const [key, value] of Object.entries(response)) {
          let parsedPeriods = [];
          let typedValue = value as AvailabilityByDayModel;

          typedValue.periods?.forEach(period => {
            this.isCalendarEmpty = false;
            parsedPeriods.push(period.period);
          });

          parsedAvailability.push({
            day: key,
            periods: parsedPeriods,
            dayShortLabel: typedValue.shortLabel
          });
        }
        this.tutorAvailability = parsedAvailability;
        this.buildPopularSlots();
        this.selectFirstAvailableDayIndex();
      },
      error: () => {
        onComplete?.();
      },
      complete: () => {
        onComplete?.();
      }
    });
  }

  private buildPopularSlots(): void {
    this.popularSlots = new Set<string>();
    for (let dayIndex = 0; dayIndex <= 4; dayIndex++) {
      this.popularSlots.add(`${dayIndex}-2`);
      this.popularSlots.add(`${dayIndex}-3`);
    }
    for (let dayIndex = 5; dayIndex <= 6; dayIndex++) {
      this.popularSlots.add(`${dayIndex}-1`);
      this.popularSlots.add(`${dayIndex}-2`);
    }
  }

  onUpdateNewAvailability(): void {
    const user = {
      id: this.authService.userDetails.id,
      tutorDetails: {
        acceptsNewStudents: this.authService.userDetails.tutorDetails._acceptsNewStudents.toString(),
      }
    } as UserObj;
    this.authService.updateUserDetails(user).subscribe(resp => {
      this.utilityService.trackEvent('availableForNewStudents', 'Successful', `${user.tutorDetails.acceptsNewStudents}`, this.authService.userDetails.id);
      this.utilityService.trackEvent('settings', 'available_for_new_students', `${user.tutorDetails.acceptsNewStudents}`, this.authService.userDetails.id);
    });
  }

  onUpdateAtHomeLessons(): void {
    const user = {
      id: this.authService.userDetails.id,
      tutorDetails: {
        offersAtHomeLessons: this.authService.userDetails.tutorDetails._offersAtHomeLessons.toString(),
      }
    } as UserObj;
    this.authService.updateUserDetails(user).subscribe();
  }

  onSelectPeriod(day, period, available): void {
    if (!this.isStudent) {
      if (this.isUpdatingAvailability) {
        return;
      }
      const periodRequest = {
        tutorId: this.tutorId,
        day: day,
        period: period
      }

      this.isUpdatingAvailability = true;
      const clearUpdating = () => {
        this.isUpdatingAvailability = false;
      };
      if (available) {
        this.bookingService.deleteTutorAvailability(periodRequest).subscribe({
          next: () => {
            this.getAvailability(clearUpdating);
          },
          error: () => {
            this.toastService.showMsg('texts.Availability_Calendar_Delete_Error', MessageType.error);
            clearUpdating();
          }
        });
      } else {
        this.bookingService.addTutorAvailability(periodRequest).subscribe({
          next: () => {
            this.getAvailability(clearUpdating);
          },
          error: () => {
            this.toastService.showMsg('texts.Availability_Calendar_Add_Error', MessageType.error);
            clearUpdating();
          }
        });
      }
    }
  }

  private selectFirstAvailableDayIndex() {
    if (this.authService.isTeacher()) {
      this.firstAvailableDayIndex = 0
    } else {
      this.firstAvailableDayIndex = this.tutorAvailability.findIndex((day) => day.periods.length > 0);
    }
  }
}